import { loadScript } from '../utils/loadScript';

const AD_SCRIPT_TIMEOUT = 5000;
const AD_RETRY_DELAY_MS = 10000;

export interface AdInitResult {
  taddy: boolean;
  adexium: boolean;
  monetag: boolean;
  gigapub: boolean;
  adsgram: boolean;
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

async function loadScriptWithRetry(
  label: string,
  url: string,
  options: Parameters<typeof loadScript>[1],
  onSuccess: () => void,
  result: AdInitResult,
  key: keyof AdInitResult,
): Promise<void> {
  // Бесконечные попытки загрузить SDK, пока не получится
  // Между попытками выдерживаем паузу AD_RETRY_DELAY_MS
  // Таймаут отдельной попытки задаётся через AD_SCRIPT_TIMEOUT
  // (options.timeout)
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await loadScript(url, options);
    if (res.success) {
      result[key] = true;
      try {
        onSuccess();
      } catch (e) {
        console.error(`[Ads] ${label} init error:`, (e as Error).message);
      }
      console.log(`[Ads] ${label} SDK loaded`);
      return;
    }

    console.warn(`[Ads] ${label} SDK failed to load, retrying in ${AD_RETRY_DELAY_MS}ms:`, res.error?.message);
    await delay(AD_RETRY_DELAY_MS);
  }
}

async function loadGigapubWithFallbacks(result: AdInitResult): Promise<void> {
  const projectId = '5098';
  const domains = ['https://ad.gigapub.tech', 'https://ru-ad.gigapub.tech'];

  // eslint-disable-next-line no-constant-condition
  while (true) {
    for (const domain of domains) {
      const url = `${domain}/script?id=${projectId}`;
      const res = await loadScript(url, { timeout: 15000 });

      if (res.success) {
        result.gigapub = true;
        console.log(`[Ads] Gigapub SDK loaded from ${domain}`);
        return;
      }

      console.warn(
        `[Ads] Gigapub SDK failed to load from ${domain}, trying next domain or retrying:`,
        res.error?.message,
      );
    }

    await delay(AD_RETRY_DELAY_MS);
  }
}

export async function initAds(): Promise<AdInitResult> {
  const result: AdInitResult = {
    taddy: false,
    adexium: false,
    monetag: false,
    gigapub: false,
    adsgram: false,
  };

  await Promise.all([
    loadScriptWithRetry(
      'Taddy',
      'https://sdk.taddy.pro/web/taddy.min.js',
      {
        timeout: AD_SCRIPT_TIMEOUT,
        attrs: { 'data-pub-id': 'd6ee8535c767c0fcd98fec3da11815c5' },
      },
      () => initTaddy(),
      result,
      'taddy',
    ),

    loadScriptWithRetry(
      'Adexium',
      'https://cdn.tgads.space/assets/js/adexium-widget.min.js',
      {
        timeout: AD_SCRIPT_TIMEOUT,
      },
      () => initAdexium(),
      result,
      'adexium',
    ),

    loadScriptWithRetry(
      'Monetag',
      'https://munqu.com/sdk.js',
      {
        timeout: AD_SCRIPT_TIMEOUT,
        attrs: { 'data-zone': '10280884', 'data-sdk': 'show_10641264' },
      },
      () => {
        // Для Monetag отдельной инициализации нет
      },
      result,
      'monetag',
    ),

    loadScriptWithRetry(
      'Adsgram',
      'https://sad.adsgram.ai/js/sad.min.js',
      {
        timeout: AD_SCRIPT_TIMEOUT,
      },
      () => {
        try {
          const Adsgram = (window as any).Adsgram;
          if (Adsgram && typeof Adsgram.init === 'function') {
            // Отдельный interstitial-блок для фонового шедулера
            const controller = Adsgram.init({ blockId: 'int-23499' });
            (window as any).adsgramInterstitialController = controller;
          }
        } catch (e) {
          console.error('[Adsgram] interstitial init error', e);
        }
      },
      result,
      'adsgram',
    ),

    // Gigapub: enhanced loader with fallback domains and infinite retries
    loadGigapubWithFallbacks(result),
  ]);

  console.log('[Ads] All SDK load attempts completed (at least once per network):', result);
  return result;
}

function initTaddy(): void {
  try {
    const Taddy = (window as any).Taddy;
    if (Taddy && typeof Taddy.ready === 'function') {
      Taddy.ready();
      console.log('[Taddy] ready() sent');
    }
  } catch (e) {
    console.error('[Taddy] ready error', e);
  }
}

function initAdexium(): void {
  try {
    const AdexiumWidget = (window as any).AdexiumWidget;
    if (!AdexiumWidget) return;

    const adexiumPush = new AdexiumWidget({
      wid: 'abe5228d-5fa3-48d1-851f-931dc91a5d88',
      adFormat: 'push-like',
      debug: false,
      firstAdImpressionIntervalInSeconds: 15,
      adImpressionIntervalInSeconds: 90,
      isFullScreen: false,
    });
    adexiumPush.autoMode();

    const adexiumInterstitial = new AdexiumWidget({
      wid: 'c8e3b41e-400e-40b1-bf10-5a06b2890f41',
      adFormat: 'interstitial',
      debug: false,
      isFullScreen: true,
    });
    (window as any).adexiumInterstitialInstance = adexiumInterstitial;

    console.log('[Adexium] widgets initialized');
  } catch (e) {
    console.error('[Adexium] init error', e);
  }
}

export function startAdsScheduler(): void {
  const MIN_INTERVAL_MS = 5 * 60 * 1000;

  let lastFullscreenAdTs = 0;
  let adsgramInterstitialDisabledUntil = 0; // timestamp до которого Adsgram interstitial не трогаем
  let fullscreenAdToggle = 0; // для чередования: Adexium сначала или Monetag+Adsgram сначала

  setInterval(async () => {
    const now = Date.now();
    if (now - lastFullscreenAdTs < MIN_INTERVAL_MS) return;

    const useAdexiumFirst = fullscreenAdToggle % 2 === 0;
    fullscreenAdToggle++;

    const tryShowAdexium = async (): Promise<boolean> => {
      const instance = (window as any).adexiumInterstitialInstance;
      if (!instance?.show) return false;
      try {
        await instance.show();
        lastFullscreenAdTs = Date.now();
        return true;
      } catch {
        return false;
      }
    };

    // Вариант полноэкранной рекламы: Monetag (основной) + Adsgram interstitial как дополнение
    const tryShowMonetag = async (): Promise<boolean> => {
      const showMonetag = (window as any).show_10641264;
      if (typeof showMonetag !== 'function') return false;
      try {
        const ymid = localStorage.getItem('telegram_id') || undefined;

        // Подготавливаем Adsgram interstitial контроллер (int-21157), если не на кулдауне
        let adsgramController: any = null;
        const nowTs = Date.now();
        const canUseAdsgram = nowTs >= adsgramInterstitialDisabledUntil;
        if (canUseAdsgram) {
          try {
            const Adsgram = (window as any).Adsgram;
            adsgramController = (window as any).adsgramInterstitialController;

            if (!adsgramController && Adsgram && typeof Adsgram.init === 'function') {
              adsgramController = Adsgram.init({ blockId: 'int-23499' });
              (window as any).adsgramInterstitialController = adsgramController;
            }
          } catch (e) {
            console.error('[Adsgram interstitial] init error in scheduler', e);
          }
        }

        const monetagPromise = (async () => {
          await showMonetag({ type: 'preload', ymid });
          await showMonetag({ ymid });
        })();

        // Adsgram запускаем с небольшой паузой (примерно через 1 секунду), если не на кулдауне.
        // Старт вызова Adsgram всегда идёт ПОСЛЕ запуска Monetag в этом тике,
        // но мы не ждём окончания показа Monetag.
        const adsgramPromise =
          canUseAdsgram && adsgramController && typeof adsgramController.show === 'function'
            ? (async () => {
                await delay(1000);
                try {
                  await adsgramController.show();
                } catch (result: any) {
                  console.warn(
                    '[Adsgram interstitial] show failed or no fill, enabling cooldown for 10 seconds',
                    result,
                  );
                } finally {
                  // После любой попытки показа (успешной или неуспешной) ставим кулдаун на 10 секунд
                  adsgramInterstitialDisabledUntil = Date.now() + 10 * 1000;
                }
              })()
            : Promise.resolve();

        const [monetagResult] = await Promise.allSettled([monetagPromise, adsgramPromise]);

        // Учитываем показ как успешный, если Monetag отработал без исключения
        if (monetagResult.status === 'fulfilled') {
          lastFullscreenAdTs = Date.now();
          return true;
        }

        return false;
      } catch {
        return false;
      }
    };

    // Чередуем: один раз сначала Adexium, потом Monetag+Adsgram;
    // в следующий тик — сначала Monetag+Adsgram, потом Adexium.
    if (useAdexiumFirst) {
      if (!(await tryShowAdexium())) await tryShowMonetag();
    } else {
      if (!(await tryShowMonetag())) await tryShowAdexium();
    }
  }, 10000);
}
